import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { patchEnableService, putServiceForm } from 'features/salesOrder/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import { TypeServiceEnum, IPassenger } from 'features/salesOrder/types';
import {
	DeactivateScenario,
	SaleOrderServiceRecord,
	amountsAreZero,
	amountsChanged,
	areAllServiceTicketsValid,
	buildLinearServicePayload,
	formatServiceAmount,
	getServiceDescription,
	getServiceFinancialSummary,
	getServiceProviderSummary,
	getServiceReferenceLabel,
	getServiceTicketLines,
	getServiceTypeLabel,
	isComplexServiceType,
	isFlightTicketServiceType,
	isLinearServiceType,
	isValidServiceReference,
} from './helpers';

interface DeactivateServiceModalProps {
	open: boolean;
	onClose: () => void;
	saleOrderId: string;
	service: SaleOrderServiceRecord | null;
	passengers: IPassenger[];
	onSuccess: () => void;
}

const CHECKBOX_LABELS = {
	understandNoService: 'Entiendo que desactivar implica que el servicio ya no se prestará al cliente.',
	understandBilling:
		'Entiendo que esto no implica automáticamente costo 0 ni venta 0: debo verificar los montos según lo que se cobrará al cliente y pagará al proveedor.',
	confirmProvider: 'Confirmo que el/los proveedor(es) del servicio son correctos.',
} as const;

export const DeactivateServiceModal = ({
	open,
	onClose,
	saleOrderId,
	service,
	passengers,
	onSuccess,
}: DeactivateServiceModalProps): JSX.Element => {
	const { auth } = useSelector((state: RootState) => state);
	const [scenario, setScenario] = useState<DeactivateScenario | ''>('');
	const [editedTotalCost, setEditedTotalCost] = useState('0');
	const [editedTotalSale, setEditedTotalSale] = useState('0');
	const [checks, setChecks] = useState({
		understandNoService: false,
		understandBilling: false,
		confirmProvider: false,
		confirmReference: false,
		confirmTickets: false,
	});
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const financialSummary = useMemo(() => (service ? getServiceFinancialSummary(service) : null), [service]);
	const providerSummary = useMemo(() => (service ? getServiceProviderSummary(service) : null), [service]);
	const isComplex = service ? isComplexServiceType(service.serviceType) : false;
	const isLinear = service ? isLinearServiceType(service.serviceType) : false;

	useEffect(() => {
		if (!open || !service || !financialSummary) {
			return;
		}

		setScenario('');
		setEditedTotalCost(String(financialSummary.totalCost));
		setEditedTotalSale(String(financialSummary.totalSaleAmount));
		setChecks({
			understandNoService: false,
			understandBilling: false,
			confirmProvider: false,
			confirmReference: false,
			confirmTickets: false,
		});
		setError(null);
		setSubmitting(false);
	}, [open, service, financialSummary]);

	const handleScenarioChange = (nextScenario: DeactivateScenario): void => {
		setScenario(nextScenario);
		setError(null);

		if (nextScenario === DeactivateScenario.NoCost && isLinear && financialSummary) {
			setEditedTotalCost('0');
			setEditedTotalSale('0');
		} else if (financialSummary) {
			setEditedTotalCost(String(financialSummary.totalCost));
			setEditedTotalSale(String(financialSummary.totalSaleAmount));
		}
	};

	const parsedEditedCost = Number(editedTotalCost);
	const parsedEditedSale = Number(editedTotalSale);
	const hasValidEditedAmounts =
		editedTotalCost !== '' &&
		editedTotalSale !== '' &&
		Number.isFinite(parsedEditedCost) &&
		Number.isFinite(parsedEditedSale) &&
		parsedEditedCost >= 0 &&
		parsedEditedSale >= 0;

	const effectiveProviderCost = isComplex ? financialSummary?.totalCost ?? 0 : parsedEditedCost;
	const hasProviderCost = effectiveProviderCost > 0;
	const referenceLabel = service ? getServiceReferenceLabel(service.serviceType) : 'Localizador / referencia';
	const isFlightWithTickets = service ? isFlightTicketServiceType(service.serviceType) : false;
	const ticketLines = useMemo(() => (service ? getServiceTicketLines(service, passengers) : []), [service, passengers]);
	const requiresTicketValidation = hasProviderCost && isFlightWithTickets && ticketLines.length > 0;
	const allTicketsValid = areAllServiceTicketsValid(ticketLines);
	const isReferenceValid = isValidServiceReference(service?.reference);
	const baseChecksConfirmed = checks.understandNoService && checks.understandBilling && checks.confirmProvider;
	const referenceChecksConfirmed = !hasProviderCost || (isReferenceValid && checks.confirmReference);
	const ticketChecksConfirmed = !requiresTicketValidation || (allTicketsValid && checks.confirmTickets);
	const allChecksConfirmed = baseChecksConfirmed && referenceChecksConfirmed && ticketChecksConfirmed;

	const complexNoCostBlocked = Boolean(
		isComplex &&
			scenario === DeactivateScenario.NoCost &&
			financialSummary &&
			!amountsAreZero(financialSummary.totalCost, financialSummary.totalSaleAmount),
	);

	const showPenaltyWarning = Boolean(
		isComplex &&
			scenario === DeactivateScenario.WithPenalty &&
			financialSummary &&
			!amountsAreZero(financialSummary.totalCost, financialSummary.totalSaleAmount),
	);

	const referenceBlocked = Boolean(hasProviderCost && scenario && !isReferenceValid);
	const ticketsBlocked = Boolean(requiresTicketValidation && scenario && !allTicketsValid);

	const canConfirm = Boolean(
		service &&
			scenario &&
			allChecksConfirmed &&
			hasValidEditedAmounts &&
			!complexNoCostBlocked &&
			!referenceBlocked &&
			!ticketsBlocked &&
			!submitting,
	);

	const handleConfirm = async (): Promise<void> => {
		if (!service || !financialSummary || !canConfirm) {
			return;
		}

		setSubmitting(true);
		setError(null);

		try {
			const shouldSaveAmounts = isLinear && amountsChanged(financialSummary, parsedEditedCost, parsedEditedSale);

			if (shouldSaveAmounts) {
				const payload = buildLinearServicePayload(service, parsedEditedCost, parsedEditedSale, auth.country);
				await putServiceForm({
					saleOrderId,
					serviceId: service.keyId,
					data: payload,
				});
			}

			await patchEnableService({
				saleOrderId,
				serviceId: service.keyId,
				enable: false,
			});

			onSuccess();
			onClose();
		} catch (err) {
			setError(extractErrorMessage(err, 'Ocurrió un error al desactivar el servicio'));
		} finally {
			setSubmitting(false);
		}
	};

	if (!service || !financialSummary || !providerSummary) {
		return <></>;
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<BlockIcon color="warning" />
				Desactivar servicio
			</DialogTitle>

			<DialogContent dividers>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
					<Box>
						<Typography variant="subtitle1" fontWeight="bold">
							{getServiceTypeLabel(service.serviceType)}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{getServiceDescription(service)}
							{service.reference ? ` · Ref: ${service.reference}` : ''}
						</Typography>
					</Box>

					<Alert severity="warning">
						<Typography variant="subtitle2" gutterBottom>
							Qué implica desactivar un servicio
						</Typography>
						<Typography variant="body2" component="div">
							<ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
								<li>
									El servicio <strong>no se prestará</strong> al cliente.
								</li>
								<li>
									<strong>No implica</strong> automáticamente que no deba cobrarse al cliente ni pagarse al proveedor.
								</li>
								<li>
									Si no se cobra nada → la venta debe estar en <strong>0</strong>.
								</li>
								<li>
									Si no se paga nada al proveedor → el costo debe estar en <strong>0</strong>.
								</li>
								<li>
									Si hay penalidad → costo = lo que paga el proveedor, venta = lo que cobramos al cliente (la venta
									puede ser 0 si asumimos el costo).
								</li>
							</ul>
						</Typography>
					</Alert>

					<Alert severity="info">
						Los montos mostrados son los <strong>guardados</strong> en la orden. Si editaste el formulario del servicio,
						guardalo antes de desactivar.
					</Alert>

					<Box>
						<Typography variant="subtitle2" gutterBottom>
							Proveedor(es)
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
							{providerSummary.tariffProvider && (
								<Typography variant="body2">
									<strong>{providerSummary.tariffProvider.label}:</strong> {providerSummary.tariffProvider.name}
								</Typography>
							)}
							{providerSummary.showBoth && providerSummary.serviceProvider && (
								<Typography variant="body2">
									<strong>{providerSummary.serviceProvider.label}:</strong> {providerSummary.serviceProvider.name}
								</Typography>
							)}
						</Box>
					</Box>

					<Box>
						<Typography variant="subtitle2" gutterBottom>
							Montos actuales guardados
						</Typography>
						<Typography variant="body2">
							Costo total: <strong>{formatServiceAmount(financialSummary.totalCost, financialSummary.currency)}</strong>
							{' · '}
							Venta total:{' '}
							<strong>{formatServiceAmount(financialSummary.totalSaleAmount, financialSummary.currency)}</strong>
						</Typography>

						{financialSummary.breakdown.length > 0 && (
							<Box sx={{ mt: 1, pl: 1 }}>
								{financialSummary.breakdown.map((line) => (
									<Typography key={line.label} variant="caption" display="block" color="text.secondary">
										{line.label}: {formatServiceAmount(line.cost, financialSummary.currency)} /{' '}
										{formatServiceAmount(line.saleAmount, financialSummary.currency)}
									</Typography>
								))}
							</Box>
						)}
					</Box>

					<Divider />

					<Box>
						<Typography variant="subtitle2" gutterBottom>
							¿Cuál es la situación?
						</Typography>
						<RadioGroup
							value={scenario}
							onChange={(event) => handleScenarioChange(event.target.value as DeactivateScenario)}
						>
							<FormControlLabel
								value={DeactivateScenario.NoCost}
								control={<Radio />}
								label="Cancelación sin costo ni cobro (costo 0, venta 0)"
							/>
							<FormControlLabel
								value={DeactivateScenario.WithPenalty}
								control={<Radio />}
								label="Cancelación con penalidad o cargo (ajustar costo y/o venta)"
							/>
							<FormControlLabel
								value={DeactivateScenario.Manual}
								control={<Radio />}
								label="Ya revisé y los montos guardados son correctos"
							/>
						</RadioGroup>
					</Box>

					{scenario === DeactivateScenario.NoCost && isComplex && complexNoCostBlocked && (
						<Alert severity="error">
							Para cancelación sin costo, los montos deben estar en 0. Ajustalos en el formulario del servicio
							{service.serviceType === TypeServiceEnum.Flight ? ' (por tipo de pasajero)' : ' (por habitación)'}, guardá
							y volvé a desactivar.
						</Alert>
					)}

					{scenario === DeactivateScenario.NoCost && isComplex && !complexNoCostBlocked && (
						<Alert severity="success">Los montos guardados ya están en 0. Podés confirmar la desactivación.</Alert>
					)}

					{showPenaltyWarning && (
						<Alert severity="warning">
							Los montos guardados siguen siendo{' '}
							<strong>
								{formatServiceAmount(financialSummary.totalCost, financialSummary.currency)} /{' '}
								{formatServiceAmount(financialSummary.totalSaleAmount, financialSummary.currency)}
							</strong>
							. Si corresponde a una penalidad distinta, ajustalos en el formulario del servicio, guardá y volvé a
							desactivar.
						</Alert>
					)}

					{isLinear && scenario && (
						<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
							<TextField
								label={`Costo total (${financialSummary.currency})`}
								type="number"
								value={editedTotalCost}
								onChange={(event) => setEditedTotalCost(event.target.value)}
								inputProps={{ min: 0, step: '0.01' }}
								disabled={scenario === DeactivateScenario.Manual}
								size="small"
								sx={{ minWidth: 180 }}
							/>
							<TextField
								label={`Venta total (${financialSummary.currency})`}
								type="number"
								value={editedTotalSale}
								onChange={(event) => setEditedTotalSale(event.target.value)}
								inputProps={{ min: 0, step: '0.01' }}
								disabled={scenario === DeactivateScenario.Manual}
								size="small"
								sx={{ minWidth: 180 }}
							/>
						</Box>
					)}

					{isLinear && scenario === DeactivateScenario.Manual && (
						<Typography variant="caption" color="text.secondary">
							En este escenario se desactivará con los montos guardados actuales, sin modificarlos desde acá.
						</Typography>
					)}

					{hasProviderCost && scenario && (
						<Box>
							<Typography variant="subtitle2" gutterBottom>
								{referenceLabel}
							</Typography>
							<Typography variant="body2">
								<strong>{service.reference?.trim() || 'Sin referencia'}</strong>
							</Typography>

							{requiresTicketValidation && (
								<Box sx={{ mt: 1.5 }}>
									<Typography variant="subtitle2" gutterBottom>
										Tickets
									</Typography>
									{ticketLines.map((line) => (
										<Typography
											key={line.passengerId}
											variant="body2"
											color={line.isValid ? 'text.primary' : 'error.main'}
										>
											{line.passengerName}: <strong>{line.ticket}</strong>
										</Typography>
									))}
								</Box>
							)}

							<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
								{requiresTicketValidation
									? `El proveedor puede usar el PNR y/o los números de ticket para el cargo de ${formatServiceAmount(
											effectiveProviderCost,
											financialSummary.currency,
									  )}.`
									: `El proveedor usará este ${referenceLabel.toLowerCase()} para el cargo de ${formatServiceAmount(
											effectiveProviderCost,
											financialSummary.currency,
									  )}.`}
							</Typography>
						</Box>
					)}

					{referenceBlocked && (
						<Alert severity="error">
							Hay costo con proveedor pero el {referenceLabel.toLowerCase()} no es válido. Corregilo en el formulario
							del servicio, guardá y volvé a desactivar.
						</Alert>
					)}

					{ticketsBlocked && (
						<Alert severity="error">
							Hay costo con proveedor pero uno o más tickets no son válidos (no pueden estar vacíos ni ser
							&quot;-&quot;). Completalos en el formulario del servicio, guardá y volvé a desactivar.
						</Alert>
					)}

					<Divider />

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
						<Typography variant="subtitle2" gutterBottom>
							Confirmaciones requeridas
						</Typography>
						{(Object.entries(CHECKBOX_LABELS) as Array<[keyof typeof checks, string]>).map(([key, label]) => (
							<FormControlLabel
								key={key}
								control={
									<Checkbox
										checked={checks[key]}
										onChange={(event) => setChecks((prev) => ({ ...prev, [key]: event.target.checked }))}
									/>
								}
								label={<Typography variant="body2">{label}</Typography>}
							/>
						))}
						{hasProviderCost && isReferenceValid && (
							<FormControlLabel
								control={
									<Checkbox
										checked={checks.confirmReference}
										onChange={(event) => setChecks((prev) => ({ ...prev, confirmReference: event.target.checked }))}
									/>
								}
								label={
									<Typography variant="body2">
										Confirmo que el {referenceLabel.toLowerCase()} <strong>{service.reference?.trim()}</strong> es
										correcto y corresponde al cargo del proveedor.
									</Typography>
								}
							/>
						)}
						{requiresTicketValidation && allTicketsValid && (
							<FormControlLabel
								control={
									<Checkbox
										checked={checks.confirmTickets}
										onChange={(event) => setChecks((prev) => ({ ...prev, confirmTickets: event.target.checked }))}
									/>
								}
								label={
									<Typography variant="body2">
										Confirmo que los números de ticket listados son correctos y corresponden al cargo del proveedor.
									</Typography>
								}
							/>
						)}
					</Box>

					{error && <Alert severity="error">{error}</Alert>}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} disabled={submitting}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					color="warning"
					onClick={() => void handleConfirm()}
					disabled={!canConfirm}
					startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <BlockIcon />}
				>
					{submitting ? 'Desactivando...' : 'Confirmar desactivación'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
